import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { DisabilityProof } from '../../../api/app-types/disability-proof.app';
import { DisabilityStatus } from '../../../api/app-types/disability-status.app';
import { DISABILITY_PROOF_SERVICE } from '../../../api/services/disability-proof.service';
import { LOGGER } from '../../logging/log.service';
import { PaginatedResponse } from '../../pagination/paginated-response';
import { ApiResponse, Metadata } from '../../../api/api-types/api-response.api';
import { NeighborContext } from '../../../api/app-types/neighbors.app';

/**
 * The resolver is used to fetch the disability proof details before the route is activated.
 * It ensures that the data is available when the component is initialized.
 * @param route - The activated route snapshot.
 * @param _state - The router state snapshot.
 * @returns The disability proof details.
 */
export const disabilityProofResolver: ResolveFn<ApiResponse<DisabilityProof, Metadata>> = (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const logger = inject(LOGGER);
	const api = inject(DISABILITY_PROOF_SERVICE);
	const proofId = route.paramMap.get('disabilityProofId')!;
	const clientId = route.paramMap.get('id')!;
	logger.log(`Fetching disability proof with ID: ${proofId}`);
	return api.getSingle(Number(clientId), Number(proofId));
};

/**
 * Resolver for fetching all disability proofs for a specific client.
 * @param route - The activated route snapshot.
 * @param _state - The router state snapshot.
 * @returns An array of disability proofs for the specified client.
 */
export const disabilityProofsResolver: ResolveFn<DisabilityProof[]> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const logger = inject(LOGGER);
	const api = inject(DISABILITY_PROOF_SERVICE);
	const env = inject(ENVIRONMENT);
	const clientId = route.paramMap.get('id');
	logger.log(`Fetching all disability proofs for client ID: ${clientId}`);
	const response = await api.getAll(Number(clientId), env.pagination.offset, env.pagination.range); // Adjust offset and range as needed
	return response.entities;
};

export const disabilityStatusResolver: ResolveFn<PaginatedResponse<DisabilityStatus>> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(DISABILITY_PROOF_SERVICE);
	const env = inject(ENVIRONMENT);
	return await api.getDisabilityStatus(env.pagination.offset, env.pagination.range);
};

export const latestDisabilityReportResolver: ResolveFn<DisabilityProof> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(DISABILITY_PROOF_SERVICE);
	const clientId = _route.paramMap.get('id');
	const response = await api.getAll(Number(clientId), 0, 2); // Adjusted offset and range for the lastest
	const sortedProofs = [...response.entities].sort((a, b) => b.since.getTime() - a.since.getTime());
	return sortedProofs[0];
};

export const disabilityProofNeighborsResolver: ResolveFn<NeighborContext<DisabilityProof>> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(DISABILITY_PROOF_SERVICE);
	const clientId = route.paramMap.get('id')!;
	const proofId = route.paramMap.get('disabilityProofId')!;
	return api.getProofNeighbors(clientId, proofId);
};
